import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, flatMap, map, mergeMap, take, tap } from 'rxjs/operators';

import { Category } from '../models/lookup-category';
import { DataField } from '../models/data-field';
import { Layout, LayoutTemplate } from '../models/layout';
import { Lookup } from '../models/lookup-category';
import { LookupType } from '../models/lookup-type';
import { Relationship } from '../models/relationship';
import { SubjectType } from '../models/subject-type';
import { User } from '../models/user';
import { DueDateType, TaskStatusType, TaskType } from '../models/task-type';

import { Template } from '../models/template';
import { CategoryService } from '../services/category.service';
import { LayoutService } from '../services/layout.service';
import { LookupService } from '../services/lookup.service';
import { LookupTypeService } from '../services/lookup-type.service';
import { RelationshipService } from '../services/relationship.service';
import { SubjectSettingService } from '../services/subject-setting.service';
import { SubjectTypeService } from '../services/subject-type.service';
import {
  DueDateTypeService,
  TaskStatusService,
  TaskTypeService,
} from '../services/task-type.service';

import { MatDialog } from '@angular/material/dialog';
import {
  MessageBoxComponent,
  MessageBoxParameters,
} from '../../components/messageBox/messageBox.component';
import { ProcessType, ProcessTypeTemplate } from '../models/process-type';
import { SubjectSetting } from '../models/subject-setting';
import { UserClaim } from '../models/user-claim';
import { DataService } from './data.service';
import { ProcessTypeService } from './process-type.service';
import { UserService } from './user.service';
import { UserTemplate } from '../models/user-template';
import { UserTemplateService } from './user-template.service';
import { StateService } from './state.service';
import { StatusService } from './status.service';
import { State } from '../models/state';
import { Status } from '../models/status';
import { UserSetting } from '../models/user-setting';
import { UserSettingService } from './user-setting.service';
import { MarkerService } from './marker.service';
import { Marker } from '../models/marker';
import { EventService } from './event.service';
import { ReferralSource } from '../models/referral-source';
import { AssessmentTypeService } from './assessment-type.service';
import { AssessmentCategory } from '../models/assessment-category';
import { AssessmentTemplate } from '../models/assessment-template';
import { AssessmentType } from '../models/assessment-type';
import { AssessmentTemplateService } from './assessment-template.service';
import { AssessmentCategoryService } from './assessment-category.service';
import { ReferralSourceService } from './referral-source.service';
import { WorkflowActionService } from './workflow-action.service';
import { WorkflowTriggerService } from './workflow-trigger.service';
import { WorkflowAction } from '../models/workflow-action';
import { WorkflowTrigger } from '../models/workflow-trigger';
import { enumReportCategory, Report } from '../models/report';
import { LookupCategoryService } from './lookup-category.service';
import { AssessmentTemplateCategoryService } from './assessment-template-category.service';
import { OrganisationService } from './organisation.service';
import { Organisation } from '../models/organisations';
import { OnlineReportSettingService } from './online-report-setting.service';
import { OnlineReportSetting } from '../models/online-reporting-setting';
import { environment } from 'environments/environment';
import { SystemReportService } from './reporting/system-report.service';
import { ReferralReportService } from './reporting/referral-report.service';
import { NavigationHelper } from './navigation-helper';
import { EvidenceUploadSettingService } from './evidence-upload-setting.service';
import { EvidenceUploadSetting } from '../models/evidence-upload-setting';
import { EmailTemplateService } from './email-template.service';
import { EmailTemplate } from '../models/email-template';
import { NoteType } from '../models/note';
import { NoteTypeService } from './note-type.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { Retention } from '../models/retention';
import { RetentionService } from './retention.service';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class DefaultDataService extends DataService<any> implements OnDestroy {
  name = 'GetDefaultData';

  maxFileUploadSizeMb: number; // Max size of file to upload in MB
  maxFileUploadLimit: number; // Max number of files to upload at once.
  autoTimeoutInMinutes: number;
  autoAttachMergedDocuments: boolean;
  autoSetManager: boolean;
  retentionDays: number;
  markerSearchCustomText = '';
  searchReferralPrimarySubjectTypeId = 1;
  defaultFontSize: number = 3;
  enableMarkerSearch = false;

  //enableServerCard = false;

  referralPrimarySubjectTypeId = 1;
  enableReferrals = false;
  enableAssessments = false;
  enableOnlineReporting = false;
  enableEvidenceUpload = false;
  enableEmailTemplates = false;
  allowedFileExtensions = '';
  requestTimeout = 120000;
  promptForTime = false;

  enableNotesAsPage = false;
  enableEmailWhenNoteCreated = false;

  serverName = '';
  serverUsername = '';
  serverPassword = '';
  serverPort: number;
  serverFromAddress = '';
  serverToAddress = '';

  enableServerCard = false;
  maxQuestionnairesPerDay: number;
  promptQuestionnaireDays: number;

  // global override to show/hide features relating to referrals, assessments and reviews
  enableReferralsAndAssessments = true;
  //override for mobile view to show/hide features.
  enableMobileView = false;
  // global override to show/hide features relating to online reporting - true to enable, false to disable
  enableOnlineReportingOverride = true;

  // global override to show/hide features relating to evidence upload/email templates - true to enable, false to disable
  enableEvidenceUploadOverride = true;

  limitAccessToSubjectsByOrg = true;
  allowAdminsToCreateOrganisations = true;

  hyperLawLicenceValid = false;
  enableCasePreparationFeatures = false;
  hyperLawApiKey = '';
  hyperLawCustomerExternalRef = '';

  timeRecordingInMinutes: boolean = false;
  enableGridSizing: boolean = false;
  enableEmailReferrals: boolean = false;
  fromEmailAddress = '';
  emailSubject = '';

  enableFTSU2WayComms: boolean = false;
  enableFTSUTranslate: boolean = false;

  enableRAF2WayComms: boolean = false;
  enableRAFTranslate: boolean = false;

  sendEmailWhenCaseReAssigned = false;

  includeClosedCasesInReviews = true;
  includeArchivedSubjectsInSearches = false;
  includeTimeForTasks = false;

  showSubjectReviewWidget = false;

  askForFeedbackUrl: string = '';
  questionnaireId: number;
  questionnaireTitle: string = '';
  enableGeoLocation: boolean = false;
  enableFOIFeature: boolean = false;
  enableBIDashboardAccess: boolean = false;
  requirePermissionToCloseCases: boolean = false;
  customerLocation: number;

  dvlaSettings: DVLASettings = new DVLASettings();

  // stash of templates to help with patching
  templates: Template[];

  onDefaultDataLoaded: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private assessmentCategoryService: AssessmentCategoryService,
    private assessmentTemplateService: AssessmentTemplateService,
    private assessmentTypeService: AssessmentTypeService,
    private assessmentTemplateCategoryService: AssessmentTemplateCategoryService,
    private subjectTypeService: SubjectTypeService,
    private lookupService: LookupService,
    private lookupTypeService: LookupTypeService,
    private relationshipService: RelationshipService,
    private categoryService: CategoryService,
    private taskTypeService: TaskTypeService,
    private emailTemplateService: EmailTemplateService,
    private layoutService: LayoutService,
    private markerService: MarkerService,
    private onlineReportSettingService: OnlineReportSettingService,
    private evidenceUploadSettingService: EvidenceUploadSettingService,
    private retentionSettingService: RetentionService,
    private referralSourceService: ReferralSourceService,
    private noteTypeService: NoteTypeService,
    private subjectSettingService: SubjectSettingService,
    private processTypeService: ProcessTypeService,
    private dueDateTypeService: DueDateTypeService,
    private referralReportService: ReferralReportService,
    private systemReportService: SystemReportService,
    private stateService: StateService,
    private statusService: StatusService,
    private taskStatusService: TaskStatusService,
    private userService: UserService,
    private userSettingService: UserSettingService,
    private userTemplateService: UserTemplateService,
    private eventService: EventService,
    private workflowActionService: WorkflowActionService,
    private workflowTriggerService: WorkflowTriggerService,
    private lookupCategoryService: LookupCategoryService,
    private organisationService: OrganisationService,
    protected router: Router
  ) {
    super(DefaultDataService, 'defaultData', router);
  }

  ngOnDestroy(): void {}

  prefetchSyncAndUpdate(): Observable<boolean> {
    const resourceUrl = 'api/prefetchSyncAndUpdate';
    this.log('Sync And Update');

    this.log('Resource log:' + resourceUrl);
    return this.http.get(resourceUrl).pipe(
      tap((_) => {
        this.log(`Sync And Update succeeded`);
      }),
      catchError(this.handleError(`Sync and Update`, true)),
      map((_) => true)
    );
  }

  public getDefaultData(): Observable<boolean> {
    this.log('Get Default Data');
    // MRD - not sure if it is necessary to reset all caches but as it is potentially an issue commented it out for now...
    // this.resetAllCache();

    return forkJoin<[boolean, boolean]>([
      this.getDefaultDataInternal(),
      this.getSettingsData(),
    ]).pipe(
      take(1),
      mergeMap((_) => {
        return this.getUserData().pipe(
          map((_) => {
            this.patch();
            return true;
          })
        );
      })
    );
  }

  public getSettingsData(): Observable<boolean> {
    const resourceUrl = 'api/getSettingsData';
    this.log('Get Settings Data');

    this.log('Resource log:' + resourceUrl);
    return this.http.get(resourceUrl).pipe(
      tap((_) => this.log(`Get Settings Data succeeded`)),
      catchError(this.handleError(`Get Settings Data`, true)),
      map((response: SettingsData) => {
        this.maxFileUploadSizeMb = response.maxFileUploadSizeMb;
        this.maxFileUploadLimit = response.maxFileUploadLimit;
        this.autoTimeoutInMinutes = response.autoTimeoutInMinutes;
        this.autoAttachMergedDocuments = response.autoAttachMergedDocuments;
        this.autoSetManager = response.autoSetManager;
        this.retentionDays = response.retentionDays;
        this.markerSearchCustomText = response.markerSearchCustomText;
        this.enableMarkerSearch =
          this.enableReferralsAndAssessments && response.enableMarkerSearch;
        this.enableReferrals =
          this.enableReferralsAndAssessments && response.enableReferrals;
        this.enableAssessments =
          this.enableReferralsAndAssessments && response.enableAssessments;
        this.enableOnlineReporting =
          this.enableOnlineReportingOverride && response.enableOnlineReporting;
        this.enableEmailTemplates =
          this.enableEvidenceUploadOverride && response.enableEmailTemplates;
        this.enableEvidenceUpload =
          this.enableEvidenceUploadOverride && response.enableEvidenceUpload;
        this.enableMobileView = response.enableMobileView;
        environment.enableMobileView = this.enableMobileView;

        this.enableEmailReferrals = response.enableEmailReferrals;
        environment.enableEmailReferrals = this.enableEmailReferrals;

        this.fromEmailAddress = response.fromEmailAddress;
        this.emailSubject = response.emailSubject;

        this.limitAccessToSubjectsByOrg =
          this.limitAccessToSubjectsByOrg &&
          response.limitAccessToSubjectsByOrg;
        this.allowAdminsToCreateOrganisations =
          this.allowAdminsToCreateOrganisations &&
          response.allowAdminsToCreateOrganisations;

        this.enableCasePreparationFeatures =
          response.enableCasePreparationFeatures;
        this.hyperLawApiKey = response.hyperLawApiKey;
        this.hyperLawCustomerExternalRef = response.hyperLawCustomerExternalRef;

        this.defaultFontSize = response.defaultFontSize;

        this.sendEmailWhenCaseReAssigned = response.sendEmailWhenCaseReAssigned;
        this.enableNotesAsPage = response.enableNotesAsPage;
        this.enableEmailWhenNoteCreated = response.enableEmailWhenNoteCreated;

        this.maxQuestionnairesPerDay = response.maxQuestionnairesPerDay;
        this.promptQuestionnaireDays = response.promptQuestionnaireDays;

        this.serverName = response.serverName;
        this.serverPort = response.serverPort;
        this.serverUsername = response.serverUsername;
        this.serverPassword = response.serverPassword;
        this.enableServerCard = response.enableServerCard;
        this.serverFromAddress = response.serverFromAddress;
        this.serverToAddress = response.serverToAddress;

        environment.limitAccessToSubjectsByOrg = this.limitAccessToSubjectsByOrg;
        environment.allowAdminsToCreateOrganisations = this.allowAdminsToCreateOrganisations;
        environment.enableCasePreparationFeatures = this.enableCasePreparationFeatures;

        this.enableGeoLocation = response.enableGeoLocation || false;
        environment.enableGeoLocation = this.enableGeoLocation;

        this.enableFOIFeature = response.enableFOIFeature || false;
        environment.enableFOIFeature = this.enableFOIFeature;
        this.customerLocation = response.customerLocation;

        this.enableBIDashboardAccess = response.enableBIDashboardAccess || false;
        environment.enableBIDashboardAccess = this.enableBIDashboardAccess;
        
        this.requirePermissionToCloseCases = response.requirePermissionToCloseCases || false;
        environment.requirePermissionToCloseCases = this.requirePermissionToCloseCases;

        this.timeRecordingInMinutes = response.timeRecordingInMinutes;
        environment.timeRecordingInMinutes = this.timeRecordingInMinutes;

        this.enableGridSizing = response.enableGridSizing;
        environment.enableGridSizing = this.enableGridSizing;

        this.enableFTSU2WayComms = response.enableFTSU2WayComms;
        environment.enableFTSU2WayComms = this.enableFTSU2WayComms;

        this.enableFTSUTranslate = response.enableFTSUTranslate;
        environment.enableFTSUTranslate = this.enableFTSUTranslate;

        this.enableRAF2WayComms = response.enableRAF2WayComms;
        environment.enableRAF2WayComms = this.enableRAF2WayComms;

        this.enableRAFTranslate = response.enableRAFTranslate;
        environment.enableRAFTranslate = this.enableRAFTranslate;

        this.includeClosedCasesInReviews = response.includeClosedCasesInReviews;
        this.includeArchivedSubjectsInSearches = response.includeArchivedSubjectsInSearches;
        this.includeTimeForTasks = response.includeTimeForTasks;

        this.showSubjectReviewWidget = response.showSubjectReviewWidget;

        this.updateLimitAccessToSubjectsByOrg(
          environment.limitAccessToSubjectsByOrg
        );
        if (response.referralPrimarySubjectTypeId) {
          this.referralPrimarySubjectTypeId =
            response.referralPrimarySubjectTypeId;
        }
        if (response.searchReferralPrimarySubjectTypeId) {
          this.searchReferralPrimarySubjectTypeId =
            response.searchReferralPrimarySubjectTypeId;
        }
        this.allowedFileExtensions = response.allowedFileExtensions;
        this.requestTimeout = response.requestTimeout;
        this.promptForTime = response.promptForTime;

        this.dvlaSettings.dvlaEnabled = response.dvlaSettings.dvlaEnabled;
        this.dvlaSettings.dvlaAPIKey = response.dvlaSettings.dvlaAPIKey;
        this.dvlaSettings.dvlaAPIUrl = response.dvlaSettings.dvlaAPIUrl;
        this.dvlaSettings.dvlaSubjectType =
          response.dvlaSettings.dvlaSubjectType;
        this.dvlaSettings.dvlaMapMake = response.dvlaSettings.dvlaMapMake;
        this.dvlaSettings.dvlaMapMotStatus =
          response.dvlaSettings.dvlaMapMotStatus;
        this.dvlaSettings.dvlaMapRegistrationNumber =
          response.dvlaSettings.dvlaMapRegistrationNumber;
        this.dvlaSettings.dvlaMapTaxStatus =
          response.dvlaSettings.dvlaMapTaxStatus;
        this.dvlaSettings.dvlaMapTaxDueDate =
          response.dvlaSettings.dvlaMapTaxDueDate;
        this.dvlaSettings.dvlaMapArtEndDate =
          response.dvlaSettings.dvlaMapArtEndDate;
        this.dvlaSettings.dvlaMapMotExpiryDate =
          response.dvlaSettings.dvlaMapMotExpiryDate;
        this.dvlaSettings.dvlaMapMonthOfFirstDvlaRegistration =
          response.dvlaSettings.dvlaMapMonthOfFirstDvlaRegistration;
        this.dvlaSettings.dvlaMapMonthOfFirstRegistration =
          response.dvlaSettings.dvlaMapMonthOfFirstRegistration;
        this.dvlaSettings.dvlaMapYearOfManufacture =
          response.dvlaSettings.dvlaMapYearOfManufacture;
        this.dvlaSettings.dvlaMapEngineCapacity =
          response.dvlaSettings.dvlaMapEngineCapacity;
        this.dvlaSettings.dvlaMapCo2Emissions =
          response.dvlaSettings.dvlaMapCo2Emissions;
        this.dvlaSettings.dvlaMapFuelType =
          response.dvlaSettings.dvlaMapFuelType;
        this.dvlaSettings.dvlaMapMarkedForExport =
          response.dvlaSettings.dvlaMapMarkedForExport;
        this.dvlaSettings.dvlaMapColour = response.dvlaSettings.dvlaMapColour;
        this.dvlaSettings.dvlaMapTypeApproval =
          response.dvlaSettings.dvlaMapTypeApproval;
        this.dvlaSettings.dvlaMapWheelplan =
          response.dvlaSettings.dvlaMapWheelplan;
        this.dvlaSettings.dvlaMapRevenueWeight =
          response.dvlaSettings.dvlaMapRevenueWeight;
        this.dvlaSettings.dvlaMapRealDrivingEmissions =
          response.dvlaSettings.dvlaMapRealDrivingEmissions;
        this.dvlaSettings.dvlaMapDateOfLastV5CIssued =
          response.dvlaSettings.dvlaMapDateOfLastV5CIssued;
        this.dvlaSettings.dvlaMapEuroStatus =
          response.dvlaSettings.dvlaMapEuroStatus;
        this.dvlaSettings.dvlaMapAutomatedVehicle =
          response.dvlaSettings.dvlaMapAutomatedVehicle;

        this.eventService.updateSettings(response);

        // Ensure Navigation updated after settings updated from webApi
        NavigationHelper.markerSearchEnabled = this.enableMarkerSearch;
        NavigationHelper.referralsEnabled = this.enableReferrals;
        NavigationHelper.assessmentsEnabled = this.enableAssessments;
        NavigationHelper.onlineReportingEnabled = this.enableOnlineReporting;
        NavigationHelper.emailTemplatesEnabled = this.enableEmailTemplates;
        NavigationHelper.evidenceUploadEnabled = this.enableEvidenceUpload;
        NavigationHelper.enableFOIFeature = this.enableFOIFeature;
        NavigationHelper.updateNavigation();

        return true;
      })
    );
  }

  public updateLimitAccessToSubjectsByOrg(val: boolean) {
    this.limitAccessToSubjectsByOrg = val;
    environment.limitAccessToSubjectsByOrg = val;
    this.eventService.updateLimitAccess(val);
  }

  private getUserData(): Observable<UserData> {
    const resourceUrl = 'api/getUserData';
    this.log('Get User Data');

    this.log('Resource log:' + resourceUrl);
    return this.http.get<UserData>(resourceUrl).pipe(
      tap((_) => this.log(`Get User Data succeeded`)),
      catchError(this.handleError(`Get User Data`, true)),
      map((response: UserData) => {
        if (response) {
          const data = response;

          // stash user claims - no service for it as yet so use a private static field to stash the values
          console.log('Saving current user details in UserService');

          var me: User = this.userService.getUserById(data.currentUserId);
          this.userService.setCurrentUserObject(me, data.userClaims);
          this.eventService.updateUser(me);

          // Check Licence Expiry days left.
          const days: LicenceExpiration[] = data.licenceExpirationDays;
          this.hyperLawLicenceValid = false;
          if (days && days.length > 0) {
            for (let i = 0; i < days.length; i++) {
              const exp: LicenceExpiration = days[i];

              if (exp.productName.toLocaleLowerCase() === 'hyperlaw')
                this.hyperLawLicenceValid = true;

              if (exp.daysUntilExpiry <= 14) {
                const info: MessageBoxParameters = new MessageBoxParameters();
                info.title = 'Product Licence Information';
                info.message =
                  `Your licence to use ${exp.productName} will expire in ${exp.daysUntilExpiry} days.` +
                  ' After this time, no one will be able to access this product.';
                this.dialog.open(MessageBoxComponent, { data: info });
              }
            }
          }
          // Tell app if HyperLaw is enabled or not
          this.eventService.updateHyperLawLicenceValid(
            this.hyperLawLicenceValid
          );

          // cache data...
          this.userSettingService.cache = data.userSettings || [];
          this.userTemplateService.cache = data.userTemplates || [];

          this.askForFeedbackUrl = data.askForFeedbackUrl;
          this.questionnaireId = data.questionnaireId;
          this.questionnaireTitle = data.questionnaireTitle;

          return data;
        }
      })
    );
  }

  private getDefaultDataInternal(): Observable<boolean> {
    const resourceUrl = 'api/getDefaultData';

    this.log('Resource log:' + resourceUrl);
    return this.http.get<DefaultData>(resourceUrl).pipe(
      tap((_) => this.log(`get DefaultData`)),
      catchError(this.handleError(`Get Default Data`, true)),
      map((response: DefaultData) => {
        this.log('Default Data loaded, storing and hydrating objects');
        const data = response;

        // if no response no point continuing, return false
        if (!data) {
          return false;
        }

        const dataFields = data.dataFields;

        // stash user claims - no service for it as yet so use a private static field to stach the values
        console.log('Saving current user details in UserService');

        this.dueDateTypeService.setDueDateTypes(data.dueDateTypesList);
        this.taskStatusService.setTaskStatusTypes(data.taskStatusTypesList);

        this.emailTemplateService.mailMergeVariableFields =
          data.mailMergeMapData;

        // link subject types and datafields...
        data.subjectTypes.map((item) => {
          item.dataFields = dataFields.filter(
            (x) => x.subjectTypeId === item.id
          );
        });

        // link process types and datafields...
        data.processTypes.map((item) => {
          item.dataFields = dataFields.filter(
            (x) => x.processTypeId === item.id
          );
        });

        // link templates to porcessTypeTemplates, and then processsTypeTemplates to processTypes...
        let templates = data.templates;
        data.processTypeTemplates.map((item) => {
          item.template = templates.find((x) => x.id === item.templateId);
        });

        const processTypeTemplates = data.processTypeTemplates;
        data.processTypes.map((item) => {
          item.processTypeTemplates = processTypeTemplates.filter(
            (x) => x.processTypeId === item.id
          );
        });

        // link templates to layouttemplates, and then layouttemplates to layouts...
        templates = data.templates;
        data.layoutTemplates.map((item) => {
          item.template = templates.find((x) => x.id === item.templateId);
        });

        const layoutTemplates = data.layoutTemplates;
        data.layouts.map((item) => {
          item.layoutTemplates = layoutTemplates.filter(
            (x) => x.layoutId === item.id
          );
        });

        this.templates = data.templates;

        // ...cache default data - we need to cache first so hydrating will work
        this.cacheDefaultData(data);

        // prime the lookupCategoryService and assessmentTemplateCategory services with data
        this.lookupCategoryService.categories = [...data.categories];
        this.lookupCategoryService.lookups = [...data.lookups];
        this.assessmentTemplateCategoryService.templates = [
          ...data.assessmentTemplates,
        ];
        this.assessmentTemplateCategoryService.categories = [
          ...data.assessmentCategories,
        ];

        // hydrate items, we do this after stashing in cache as the hydration uses lookups which will be faster with cached data
        const hydrate$ = [];
        // ...iterate through the items to hydrate and push the observables into the hydrate$ array
        data.relationships.map((item) => {
          hydrate$.push(this.relationshipService.hydrate(item));
        });
        data.assessmentTypes.map((item) => {
          hydrate$.push(this.assessmentTypeService.hydrate(item));
        });
        data.assessmentTemplates.map((item) => {
          hydrate$.push(this.assessmentTemplateService.hydrate(item));
        });
        data.assessmentCategories.map((item) => {
          hydrate$.push(this.assessmentCategoryService.hydrate(item));
        });
        data.categories.map((item) => {
          hydrate$.push(this.categoryService.hydrate(item));
        });
        data.lookups.map((item) => {
          hydrate$.push(this.lookupService.hydrate(item));
        });
        data.lookupTypes.map((item) => {
          hydrate$.push(this.lookupTypeService.hydrate(item));
        });
        data.markers.map((item) => {
          hydrate$.push(this.markerService.hydrate(item));
        });
        data.reports
          .filter((i) => i.reportCategory === enumReportCategory.System)
          .map((item) => {
            hydrate$.push(this.systemReportService.hydrate(item));
          });
        data.reports
          .filter((i) => i.reportCategory === enumReportCategory.Referral)
          .map((item) => {
            hydrate$.push(this.referralReportService.hydrate(item));
          });
        data.taskTypes.map((item) => {
          hydrate$.push(this.taskTypeService.hydrate(item));
        });
        data.layouts.map((item) => {
          hydrate$.push(this.layoutService.hydrate(item));
        });
        data.subjectSettings.map((item) => {
          hydrate$.push(this.subjectSettingService.hydrate(item));
        });
        data.statuses.map((item) => {
          hydrate$.push(this.statusService.hydrate(item));
        });
        data.organisations.map((item) => {
          hydrate$.push(this.organisationService.hydrate(item));
        });
        data.onlineReportSettings.map((item) => {
          hydrate$.push(this.onlineReportSettingService.hydrate(item));
        });
        data.evidenceUploadSettings.map((item) => {
          hydrate$.push(this.evidenceUploadSettingService.hydrate(item));
        });
        data.retentionSettings.map((item) =>
          hydrate$.push(this.retentionSettingService.hydrate(item))
        );        
        data.emailTemplates.map((item) => {
          hydrate$.push(this.emailTemplateService.hydrate(item));
        });
        data.usersList.map((item) => {
          hydrate$.push(this.userService.hydrate(item));
        });
        data.noteTypes.map((item) => {
          hydrate$.push(this.noteTypeService.hydrate(item));
        });

        // ...we can't call forkJoin on an empty array so add a dummy observable
        hydrate$.push(of(''));
        // ...and now use forkJoin to 'subscribe' to the observables in the array.  This will wait until all observables have completed
        forkJoin(hydrate$)
          .pipe(take(1))
          .subscribe((_) => {
            // ...and now we have hydrated data, we cache the new values to reflect any changes due to hydration
            this.cacheDefaultData(data);

            this.log('Default Data Obtained');

            // notify subscribers that default data has been loaded
            this.onDefaultDataLoaded.next(true);

            // MRD - now that we have default data we need to trigger the onChanged event of the SubjectType and ProcessType service
            // to ensure the navbar is updated for the first time.  This should be quick as we have just cached the data :)
            this.subjectTypeService.getAll();
            this.processTypeService.getAll();
            this.organisationService.getAll();
            this.retentionSettingService.getAll();
          });
        return true;
      })
    );
  }

  // the user templates and user settings can only be hydrated once we have default data
  // therefore moved into separate method and called when other other observables have completed
  private patch() {
    const hydrate$ = [];
    const userSettings = this.userSettingService.cache || [];
    const userTemplates = this.userTemplateService.cache || [];

    if (this.templates.length && userTemplates.length) {
      userTemplates.map((item) => {
        item.template = this.templates.find((x) => x.id === item.templateId);
      });
    }
    if (userSettings.length) {
      userSettings.map((item) => {
        hydrate$.push(this.userSettingService.hydrate(item));
      });
    }
    if (userTemplates.length) {
      userTemplates.map((item) => {
        hydrate$.push(this.userTemplateService.hydrate(item));
      });
    }
    hydrate$.push(of(''));
    forkJoin(hydrate$).pipe(take(1)).subscribe();
  }

  cacheDefaultData(data: DefaultData) {
    // stash data in relevant data caches
    this.assessmentCategoryService.cache = data.assessmentCategories;
    this.assessmentTemplateService.cache = data.assessmentTemplates;
    this.assessmentTypeService.cache = data.assessmentTypes;
    this.categoryService.cache = data.categories;
    this.emailTemplateService.cache = data.emailTemplates;
    this.evidenceUploadSettingService.cache = data.evidenceUploadSettings;
    this.retentionSettingService.cache = data.retentionSettings;
    this.layoutService.cache = data.layouts;
    this.lookupService.cache = data.lookups;
    this.lookupTypeService.cache = data.lookupTypes;
    this.markerService.cache = data.markers;
    this.organisationService.cache = data.organisations;
    this.onlineReportSettingService.cache = data.onlineReportSettings;
    this.processTypeService.cache = data.processTypes;
    this.referralSourceService.cache = data.referralSources;
    this.relationshipService.cache = data.relationships;
    this.systemReportService.cache = data.reports.filter(
      (i) => i.reportCategory === enumReportCategory.System
    );
    this.referralReportService.cache = data.reports.filter(
      (i) => i.reportCategory === enumReportCategory.Referral
    );
    this.stateService.cache = data.states;
    this.statusService.cache = data.statuses;
    this.subjectSettingService.cache = data.subjectSettings;
    this.subjectTypeService.cache = data.subjectTypes;
    this.noteTypeService.cache = data.noteTypes;
    this.taskTypeService.cache = data.taskTypes;
    this.userService.cache = data.usersList;
    this.workflowActionService.cache = data.workflowActions;
    this.workflowTriggerService.cache = data.workflowTriggers;
  }

  public getAddressSubjectCount(): Observable<number> {
    const resourceUrl = 'api/getAddressSubjectCount';
    return this.http.get(resourceUrl).pipe(
      catchError(this.handleError(`Get Address Subject Count`, true)),
      map((response: number) => {
        return response;
      })
    );
  }

  public getAddressNoLocationSubjectCount(): Observable<number> {
    const resourceUrl = 'api/getAddressNoLocationSubjectCount';
    return this.http.get(resourceUrl).pipe(
      catchError(this.handleError(`getAddressNoLocationSubjectCount`, true)),
      map((response: number) => {
        return response;
      })
    );
  }

  public updateReportViews(): Observable<boolean> {
    const resourceUrl = 'api/updateReportViews';
    return this.http.get(resourceUrl).pipe(
      catchError(this.handleError(`updateReportViews`, true)),
      map((response: boolean) => {
        return response;
      })
    );
  }
}

// tslint:disable-next-line:max-classes-per-file
export class DefaultData {
  subjectTypes: SubjectType[];
  lookups: Lookup[];
  lookupTypes: LookupType[];
  categories: Category[];
  markers: Marker[];
  organisations: Organisation[];
  emailTemplates: EmailTemplate[];
  evidenceUploadSettings: EvidenceUploadSetting[];
  retentionSettings: Retention[];
  onlineReportSettings: OnlineReportSetting[];
  processTypes: ProcessType[];
  processTypeTemplates: ProcessTypeTemplate[];
  referralSources: ReferralSource[];
  relationships: Relationship[];
  assessmentCategories: AssessmentCategory[];
  assessmentTemplates: AssessmentTemplate[];
  assessmentTypes: AssessmentType[];
  taskTypes: TaskType[];
  dataFields: DataField[];
  layouts: Layout[];
  layoutTemplates: LayoutTemplate[];
  reports: Report[];
  templates: Template[];
  subjectSettings: SubjectSetting[];
  dueDateTypesList: DueDateType[];
  states: State[];
  statuses: Status[];
  taskStatusTypesList: TaskStatusType[];
  usersList: User[];
  workflowActions: WorkflowAction[];
  workflowTriggers: WorkflowTrigger[];
  mailMergeMapData: string;
  noteTypes: NoteType[];
}

// tslint:disable-next-line: max-classes-per-file
export class UserData {
  currentUserId: number;
  currentUserUid: string;
  licenceExpirationDays: LicenceExpiration[];
  userClaims: UserClaim[];
  userSettings: UserSetting[];
  userTemplates: UserTemplate[];
  askForFeedbackUrl: string = '';
  questionnaireId: number;
  questionnaireTitle: string = '';

  constructor(d: any) {
    if (d != null && d != undefined) {
      this.currentUserId = d.currentUserId;
      this.currentUserUid = d.currentUserUid;
      this.userClaims = d.userClaims;
      this.userSettings = d.userSettings;
      this.userTemplates = d.userTemplates;
      this.askForFeedbackUrl = d.askForFeedbackUrl;
      this.questionnaireId = d.questionnaireId;
      this.questionnaireTitle = d.questionnaireTitle;
    }
  }
}

export class LicenceExpiration {
  productName: string;
  daysUntilExpiry: number;
}

// tslint:disable-next-line: max-classes-per-file
export class SettingsData {
  maxFileUploadSizeMb: number;
  maxFileUploadLimit: number;
  autoTimeoutInMinutes: number;
  autoAttachMergedDocuments: boolean;
  autoSetManager: boolean;
  retentionDays: number;
  markerSearchCustomText: string;
  enableServerCard: boolean;
  serverName: string;
  serverPort: number;
  serverUsername: string;
  serverPassword: string;
  serverFromAddress: string;
  serverToAddress: string;
  searchReferralPrimarySubjectTypeId: number;
  enableMarkerSearch: boolean;
  enableAssessments: boolean;
  enableOnlineReporting: boolean;
  enableReferrals: boolean;
  enableEmailTemplates: boolean;
  enableMobileView: boolean;
  enableEvidenceUpload: boolean;
  referralPrimarySubjectTypeId: number;
  allowedFileExtensions: string;
  requestTimeout: number;
  promptForTime: boolean;
  limitAccessToSubjectsByOrg: boolean;
  allowAdminsToCreateOrganisations: boolean;
  enableCasePreparationFeatures: boolean;
  hyperLawApiKey: string;
  hyperLawCustomerExternalRef: string;
  enableEmailWhenNoteCreated: boolean;
  enableNotesAsPage: boolean;
  defaultFontSize: number;
  sendEmailWhenCaseReAssigned: boolean;
  timeRecordingInMinutes: boolean;
  enableGridSizing: boolean;
  enableEmailReferrals: boolean;
  fromEmailAddress: string;
  emailSubject: string;

  enableFTSU2WayComms: boolean;
  enableFTSUTranslate: boolean;

  enableRAF2WayComms: boolean;
  enableRAFTranslate: boolean;

  includeClosedCasesInReviews: boolean;
  includeArchivedSubjectsInSearches: boolean;
  includeTimeForTasks: boolean;
  showSubjectReviewWidget: boolean;
  maxQuestionnairesPerDay: number;
  promptQuestionnaireDays: number;
  enableGeoLocation: boolean;
  enableFOIFeature: boolean;
  customerLocation: number;
  enableBIDashboardAccess: boolean;
  requirePermissionToCloseCases: boolean;

  dvlaSettings: DVLASettings;
}

export class DVLASettings {
  dvlaEnabled: boolean;
  dvlaAPIKey: string;
  dvlaAPIUrl: number;
  dvlaSubjectType: number;

  dvlaMapRegistrationNumber: number;
  dvlaMapMotStatus: number;
  dvlaMapMake: number;
  dvlaMapTaxStatus: number;
  dvlaMapTaxDueDate: number;
  dvlaMapArtEndDate: number;
  dvlaMapMotExpiryDate: number;
  dvlaMapMonthOfFirstDvlaRegistration: number;
  dvlaMapMonthOfFirstRegistration: number;
  dvlaMapYearOfManufacture: number;
  dvlaMapEngineCapacity: number;
  dvlaMapCo2Emissions: number;
  dvlaMapFuelType: number;
  dvlaMapMarkedForExport: number;
  dvlaMapColour: number;
  dvlaMapTypeApproval: number;
  dvlaMapWheelplan: number;
  dvlaMapRevenueWeight: number;
  dvlaMapRealDrivingEmissions: number;
  dvlaMapDateOfLastV5CIssued: number;
  dvlaMapEuroStatus: number;
  dvlaMapAutomatedVehicle: number;

  constructor(data: any = null) {
    if (data) {
      this.dvlaEnabled = data.dvlaEnabled;
      this.dvlaAPIKey = data.dvlaAPIKey;
      this.dvlaAPIUrl = data.dvlaAPIUrl;
      this.dvlaSubjectType = data.dvlaSubjectType;

      this.dvlaMapRegistrationNumber = data.dvlaMapRegistrationNumber;
      this.dvlaMapMotStatus = data.dvlaMapMotStatus;
      this.dvlaMapMake = data.dvlaMapMake;
      this.dvlaMapTaxStatus = data.dvlaMapTaxStatus;
      this.dvlaMapTaxDueDate = data.dvlaMapTaxDueDate;
      this.dvlaMapArtEndDate = data.dvlaMapArtEndDate;
      this.dvlaMapMotExpiryDate = data.dvlaMapMotExpiryDate;
      this.dvlaMapMonthOfFirstDvlaRegistration =
        data.dvlaMapMonthOfFirstDvlaRegistration;
      this.dvlaMapMonthOfFirstRegistration =
        data.dvlaMapMonthOfFirstRegistration;
      this.dvlaMapYearOfManufacture = data.dvlaMapYearOfManufacture;
      this.dvlaMapEngineCapacity = data.dvlaMapEngineCapacity;
      this.dvlaMapCo2Emissions = data.dvlaMapCo2Emissions;
      this.dvlaMapFuelType = data.dvlaMapFuelType;
      this.dvlaMapMarkedForExport = data.dvlaMapMarkedForExport;
      this.dvlaMapColour = data.dvlaMapColour;
      this.dvlaMapTypeApproval = data.dvlaMapTypeApproval;
      this.dvlaMapWheelplan = data.dvlaMapWheelplan;
      this.dvlaMapRevenueWeight = data.dvlaMapRevenueWeight;
      this.dvlaMapRealDrivingEmissions = data.dvlaMapRealDrivingEmissions;
      this.dvlaMapDateOfLastV5CIssued = data.dvlaMapDateOfLastV5CIssued;
      this.dvlaMapEuroStatus = data.dvlaMapEuroStatus;
      this.dvlaMapAutomatedVehicle = data.dvlaMapAutomatedVehicle;
    }
  }

  public resetMappings() {
    this.dvlaMapRegistrationNumber = -1;
    this.dvlaMapMotStatus = -1;
    this.dvlaMapMake = -1;
    this.dvlaMapTaxStatus = -1;
    this.dvlaMapTaxDueDate = -1;
    this.dvlaMapArtEndDate = -1;
    this.dvlaMapMotExpiryDate = -1;
    this.dvlaMapMonthOfFirstDvlaRegistration = -1;
    this.dvlaMapMonthOfFirstRegistration = -1;
    this.dvlaMapYearOfManufacture = -1;
    this.dvlaMapEngineCapacity = -1;
    this.dvlaMapCo2Emissions = -1;
    this.dvlaMapFuelType = -1;
    this.dvlaMapMarkedForExport = -1;
    this.dvlaMapColour = -1;
    this.dvlaMapTypeApproval = -1;
    this.dvlaMapWheelplan = -1;
    this.dvlaMapRevenueWeight = -1;
    this.dvlaMapRealDrivingEmissions = -1;
    this.dvlaMapDateOfLastV5CIssued = -1;
    this.dvlaMapEuroStatus = -1;
    this.dvlaMapAutomatedVehicle = -1;
  }
}
